import { apiRequest } from './client'
import type { FileKey } from '@/types/bundle'
import type { DiffResult, DiffStatus } from '@/types/diff'

interface DiffChangeResponse {
  path: string
  old_value: unknown
  new_value: unknown
}

interface DiffFileEntryResponse {
  file_key: FileKey
  status: DiffStatus
  changes: DiffChangeResponse[]
}

interface DiffPromptEntryResponse {
  filename: string
  status: DiffStatus
  old_content: string | null
  new_content: string | null
}

interface DiffResponse {
  from: string
  to: string
  files: DiffFileEntryResponse[]
  // PRD 29 - present alongside files[] once that branch's schema additions
  // are live; optional here so this doesn't break against an older backend
  // still missing it (falls back to an empty array, never a crash).
  prompts?: DiffPromptEntryResponse[]
}

// GET /gui/v1/diff?from=&to= (PRD 23, prompts[] added by PRD 29) - always
// all 7 file_keys, changes[] pre-sorted by dotted path server-side; prompts[]
// is one entry per real prompts/ filename present on either side.
export async function getDiff(from: string, to: string): Promise<DiffResult> {
  const response = await apiRequest<DiffResponse>(
    `/gui/v1/diff?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  )
  return {
    from: response.from,
    to: response.to,
    files: response.files.map((file) => ({
      fileKey: file.file_key,
      status: file.status,
      changes: file.changes.map((change) => ({
        path: change.path,
        oldValue: change.old_value,
        newValue: change.new_value,
      })),
    })),
    prompts: (response.prompts ?? []).map((prompt) => ({
      filename: prompt.filename,
      status: prompt.status,
      oldContent: prompt.old_content,
      newContent: prompt.new_content,
    })),
  }
}
